import React from "react";
import { useNavigate } from "react-router-dom";

function isIOSStandalone() {
  const standalone =
    typeof navigator !== "undefined" && navigator.standalone === true;
  const displayStandalone =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches;
  return Boolean(standalone || displayStandalone);
}
function hasNativeBridge() {
  try {
    return !!(
      window &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.openExternal
    );
  } catch {
    return false;
  }
}
function openViaNativeBridge(url) {
  try {
    window.webkit.messageHandlers.openExternal.postMessage(url);
    return true;
  } catch {
    return false;
  }
}

function isSpecialScheme(url) {
  return /^(tel:|mailto:|sms:|facetime:|whatsapp:)/i.test(url);
}

function isAbsoluteHttp(url) {
  return /^https?:\/\//i.test(url);
}
function toWhatsAppDeepLink(url) {
  try {
    const u = new URL(url);
    if (!/api\.whatsapp\.com$/i.test(u.hostname)) return null;
    const phone = u.searchParams.get("phone");
    const text = u.searchParams.get("text") || "";
    if (!phone) return null;
    const deep = new URL("whatsapp://send");
    deep.searchParams.set("phone", phone);
    if (text) deep.searchParams.set("text", text);
    return deep.toString();
  } catch {
    return null;
  }
}

// ⬅️ key util: do everything possible to open EXTERNALLY (Safari/app)
function openInExternalBrowser(url) {
  // Prefer native bridge (WKWebView container)
  if (hasNativeBridge()) {
    openViaNativeBridge(url);
    return;
  }

  // Special schemes: let OS handle
  if (isSpecialScheme(url)) {
    window.location.href = url;
    return;
  }

  // WhatsApp web -> deep link first, else fallback to https (still try to open externally)
  const deep = toWhatsAppDeepLink(url);
  if (deep) {
    // try deep first
    const aDeep = document.createElement("a");
    aDeep.href = deep;
    aDeep.target = "_blank";
    aDeep.rel = "noopener noreferrer";
    document.body.appendChild(aDeep);
    aDeep.click();
    aDeep.remove();
    // fallback to https shortly after if app didn’t open
    setTimeout(() => {
      const aWeb = document.createElement("a");
      aWeb.href = url;
      aWeb.target = "_blank";
      aWeb.rel = "noopener noreferrer";
      document.body.appendChild(aWeb);
      aWeb.click();
      aWeb.remove();
    }, 800);
    return;
  }

  // Try a real anchor click (most reliable way to kick Safari from PWA)
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Fallbacks
  if (!isIOSStandalone()) {
    // normal browsers: ensure a new tab opens
    window.open(url, "_blank", "noopener,noreferrer");
  }
  // iOS PWA: DO NOT replace location (keeps you in PWA). We skip it because you asked for *external* only.
}

const SafeLink = ({
  href,
  isInternal, // true => SPA navigate; false => open external browser
  className,
  children,
  onClick,
  preferNativeAnchorForSpecial,
}) => {
  const nav = useNavigate();
  const abs = isAbsoluteHttp(href);
  const treatInternal = typeof isInternal === "boolean" ? isInternal : !abs;

  // Optional: let native anchor own tel:/mailto:/etc for accessibility
  if (preferNativeAnchorForSpecial && isSpecialScheme(href)) {
    return (
      <a
        href={href}
        className={className}
        rel="noopener noreferrer"
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  const handleClick = (e) => {
    onClick?.(e);

    if (!treatInternal) {
      // 🔥 force external browser for is_internal=false
      e.preventDefault();
      openInExternalBrowser(href);
      return;
    }

    // Internal SPA
    if (!abs) {
      e.preventDefault();
      nav(href);
      return;
    }

    // Absolute but still marked internal (rare) -> SPA fallback
    e.preventDefault();
    nav(href.replace(window.location.origin, ""));
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={className}
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

export default SafeLink;
